import { NOTIFICATION_TYPES, openNotification } from "../Components/Notifications/NotificationsUtils";
import * as excel from 'exceljs';
import { saveAs } from 'file-saver';

export enum MimeType {
    docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pdf = 'application/pdf'
}

export async function downloadFile(request: Promise<Blob>, fileName: string, type : string = '') {
    request.then(file => {
        if (type === '') {
            type = file.type;
        }
        const blob = new Blob([file], {type} );
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.setAttribute('type', 'hidden');
        a.href = blobUrl;
        a.download = fileName;
        a.click();
        a.remove();
        downloadSucceeded();

    })
    .catch(downloadFailed);
}

const downloadFailed = (_err: any) => {
    openNotification(
        'Eroare',
        'Descărcarea nu s-a realizat cu succes!',
        NOTIFICATION_TYPES.ERROR
    );
};

const downloadSucceeded = () => {
    openNotification(
        'Succes',
        'Descărcarea s-a realizat cu succes!',
        NOTIFICATION_TYPES.SUCCESS
    );
};

export async function downloadExcel(fileName: string, columns: { header: string, key: string }[], objs?: any[] | null) {
    if (objs === undefined || objs === null) {
        return;
    }

    const workbook = new excel.Workbook();
    const sheet = workbook.addWorksheet(fileName);
    sheet.columns = columns;
    sheet.addRows(objs);
    adjustColumnWidth(sheet);
    
    const excelBuffer = await workbook.xlsx.writeBuffer();

    saveAs(
        new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        `${fileName}.xlsx`
    );
};

export interface ExcelSheetColumn {
    header: string;
    key: string;
    // latime fixa + wrap, pentru coloane cu text lung; fara ea latimea se ajusteaza dupa continut
    width?: number;
}

export interface ExcelSheet {
    name: string;
    columns: ExcelSheetColumn[];
    rows: any[];
    // randuri de antet scrise deasupra tabelului (ex. numele complet al programului): numele foii
    // e limitat la 31 de caractere, deci numele lungi ajung identice dupa taiere si nu se pot
    // deosebi din tab-uri
    headerLines?: string[];
}

const EXCEL_SHEET_NAME_MAX_LENGTH = 31;

// Excel accepta maxim 31 de caractere, fara \ / ? * [ ] :, fara apostrof la capete,
// si nume unice (case-insensitive) in acelasi workbook.
const toUniqueSheetName = (name: string, usedNames: Set<string>) => {
    const base = (name || 'Foaie')
        .replace(/[\\/?*[\]:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^'+|'+$/g, '') || 'Foaie';

    let candidate = base.substring(0, EXCEL_SHEET_NAME_MAX_LENGTH).trim();
    let index = 2;
    while (usedNames.has(candidate.toLowerCase())) {
        const suffix = ` (${index++})`;
        candidate = base.substring(0, EXCEL_SHEET_NAME_MAX_LENGTH - suffix.length).trim() + suffix;
    }

    usedNames.add(candidate.toLowerCase());
    return candidate;
};

export async function downloadExcelWorkbook(fileName: string, sheets: ExcelSheet[]) {
    const workbook = new excel.Workbook();
    const usedNames = new Set<string>();

    sheets.forEach(sheetData => {
        const sheet = workbook.addWorksheet(toUniqueSheetName(sheetData.name, usedNames));
        const headerLines = sheetData.headerLines ?? [];

        // fara `header` in definitia coloanelor, ca sa putem scrie noi antetul dupa liniile de titlu
        sheet.columns = sheetData.columns.map(({ key }) => ({ key }));

        headerLines.forEach((line, index) => {
            const row = sheet.addRow([line]);
            row.font = index === 0 ? { bold: true, size: 12 } : { italic: true };
        });

        if (headerLines.length > 0) {
            sheet.addRow([]);
        }

        sheet.addRow(sheetData.columns.map(column => column.header)).font = { bold: true };
        sheet.addRows(sheetData.rows);

        sheetData.columns.forEach((column, index) => {
            const sheetColumn = sheet.getColumn(index + 1);

            if (column.width) {
                sheetColumn.width = column.width;
                sheetColumn.alignment = { wrapText: true, vertical: 'top' };
                return;
            }

            // latimea se calculeaza doar din antetul de coloana si din date; liniile de titlu sunt
            // lungi si stau pe prima coloana, deci ar largi-o inutil
            const dataLengths = sheetData.rows.map(dataRow => `${dataRow?.[column.key] ?? ''}`.length);
            sheetColumn.width = Math.min(Math.max(column.header.length, ...dataLengths) + 2, 60);
        });
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();

    saveAs(
        new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        `${fileName}.xlsx`
    );
};

export function adjustColumnWidth(worksheet: excel.Worksheet) {
    worksheet.columns.forEach(column => {
      const lengths = column?.values?.map(v => v?.toString().length ?? 0) ?? [];
      const maxLength = Math.max(...lengths.filter(v => typeof v === 'number'));
      column.width = maxLength;
    });
  }